import { useMemo } from "react";
import type { StonePattern, StonePatternBand } from "../../../shared/api-contract";
import { thumbGeometry } from "../patternUi";
import type { ThumbGeometry } from "../patternUi";

/**
 * A stone pattern drawn flat: the band unrolled, stones as discs at exactly the
 * positions and diameters `expandStonePattern` returns (via `thumbGeometry`),
 * recesses as filled outlines, lines beside the stones as strokes. Drawn live
 * from the contract's generator — no images, no network.
 *
 * A long pattern (an eternity) is folded into two or three strips so it reads
 * as stones rather than a hairline; each strip is a nested <svg> window onto
 * the same drawing, so nothing is re-positioned.
 */
export function PatternThumb({
  pattern,
  band,
  highlightLayer,
  className = "",
  title,
  geometry,
}: {
  pattern: StonePattern | null | undefined;
  band: StonePatternBand;
  /** Outline this layer's stones in the accent (the layer being edited). */
  highlightLayer?: number | null;
  className?: string;
  title?: string;
  /** Precomputed geometry (the library caches one per entry). */
  geometry?: ThumbGeometry;
}) {
  const g = useMemo(() => geometry ?? thumbGeometry(pattern, band), [geometry, pattern, band]);
  const W = g.widthMM;
  const gap = W * 0.28;
  const seg = g.length / g.rows;
  const totalH = g.rows * W + (g.rows - 1) * gap;
  const stroke = Math.max(0.035, Math.min(0.08, W * 0.014));

  // The whole drawing, shown once per strip through that strip's window. Drawn
  // inline rather than through <use href>: an id would have to be unique
  // across every thumbnail on the page, and a collision silently shows the
  // wrong pattern.
  const drawing = (
    <>
      <rect x={0} y={0} width={fmt(g.length)} height={fmt(W)} fill="#efe9dd" />
      <rect x={0} y={0} width={fmt(g.length)} height={fmt(W * 0.14)} fill="#f7f3ea" />
      {g.pockets.map((d, i) => (
        <path key={`p${i}`} d={d} fill="#ddd1b8" stroke="#b9a882" strokeWidth={fmt(stroke)} strokeLinejoin="round" />
      ))}
      {g.rails.map((rl, i) => (
        <path key={`r${i}`} d={rl.d} fill="none" stroke="#b09a6c" strokeWidth={fmt(rl.widthMM)} strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {g.stones.map((s) => {
        const hot = highlightLayer != null && s.layer === highlightLayer;
        return (
          <g key={s.index}>
            <circle
              data-s={s.index}
              cx={fmt(s.cx)}
              cy={fmt(s.cy)}
              r={fmt(s.r)}
              fill="#ffffff"
              stroke={hot ? "#946f2e" : "#3d3d3a"}
              strokeWidth={fmt(hot ? stroke * 1.8 : stroke)}
            />
            <circle cx={fmt(s.cx)} cy={fmt(s.cy)} r={fmt(s.r * 0.5)} fill="none" stroke="#c9c6be" strokeWidth={fmt(stroke * 0.6)} />
          </g>
        );
      })}
    </>
  );

  return (
    <svg
      data-thumb-stones={g.stones.length}
      viewBox={`0 0 ${fmt(seg)} ${fmt(totalH)}`}
      className={className}
      role="img"
      aria-label={title ?? `${g.stones.length} stones`}
      preserveAspectRatio="xMidYMid meet"
    >
      {Array.from({ length: g.rows }, (_, row) => (
        <svg
          key={row}
          x={0}
          y={fmt(row * (W + gap))}
          width={fmt(seg)}
          height={fmt(W)}
          viewBox={`${fmt(row * seg)} 0 ${fmt(seg)} ${fmt(W)}`}
          data-strip={row}
        >
          {drawing}
        </svg>
      ))}
    </svg>
  );
}

const fmt = (v: number) => String(Math.round(v * 1000) / 1000);
